import { AssessmentInfo } from "@verity/api-client/types";
import { useCallback, useMemo, useState } from "react";
import { UseEditQuizStateProps } from "./useEditQuizState";
import {
	areProctoringOptionsSet,
	hasAssessmentConfigurationChanged,
	hasProctoringOptionsChanged,
} from "../../editQuizHelper";

type SaveParameters = {
	password?: string;
	additionalAssessmentInfo?: Partial<AssessmentInfo>;
};

type UseSubmitStateProps = Pick<
	UseEditQuizStateProps,
	| "logger"
	| "quizInfo"
	| "currentProfileId"
	| "proctoringAssessmentConfiguration"
	| "proctoringOption"
	| "api"
	| "initialProctoringAssessmentConfiguration"
	| "initialProctoringOption"
	| "defaultProctoringOption"
>;

/**Any state that has to do with saving the quiz or closing the quiz. */
export default function useSubmitState({
	logger,
	quizInfo,
	currentProfileId,
	proctoringAssessmentConfiguration,
	proctoringOption,
	api,
	initialProctoringAssessmentConfiguration,
	initialProctoringOption,
	defaultProctoringOption,
}: UseSubmitStateProps) {
	const { saveProctoringAssessmentConfiguration } = api;
	/* Stateful variable to call the saving handler */
	const [saveRequested, setSaveRequested] = useState<boolean>(false);
	const [finishedPresave, setFinishedPresave] = useState<boolean>(false);
	const [saveParameters, setSaveParameters] = useState<SaveParameters>({
		password: undefined,
		additionalAssessmentInfo: undefined,
	});
	/* Pass in a promise we resolve/reject upon the user accepting the disclaimer */
	const [savePromise, setSavePromise] = useState<PromiseWithResolvers<void>>(Promise.withResolvers<void>());

	/* Saving reset function & UseEffect wrapper for component calls*/
	const [callReset, setCallReset] = useState<boolean>(false);

	const [noConfigurationsEnabledModalOpen, setNoConfigurationsEnabledModalOpen] = useState(false);
	const [confirmSaveModalResolve, setConfirmSaveModalResolve] = useState<((value: boolean) => void) | null>(null);

	const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false);
	const [confirmCloseModalResolve, setConfirmCloseModalResolve] = useState<((value: boolean) => void) | null>(null);
	const [disclaimerModalOpen, setDisclaimerModalOpen] = useState<boolean>(false);

	const resetSaveStates = useCallback(() => {
		try {
			savePromise.promise.catch((e) => console.error(e, "Resetting Promise"));
			savePromise.reject();
		} catch (e) {
			console.error(e, "Resetting Promise");
		}

		setSaveRequested(false);
		setFinishedPresave(false);
		setSavePromise(Promise.withResolvers<void>());
		setSaveParameters({
			password: undefined,
			additionalAssessmentInfo: undefined,
		});
		setCallReset(false);
	}, [savePromise, setSaveRequested, setFinishedPresave, setSavePromise, setSaveParameters, setCallReset]);

	const completePresave = useCallback(() => {
		setFinishedPresave(true);
	}, []);

	const confirmSave = useCallback(() => {
		return new Promise<boolean>((resolve) => {
			setNoConfigurationsEnabledModalOpen(true);
			void logger?.assessment.edit.configurationModal.warningShown("No Configurations Enabled");
			setConfirmSaveModalResolve(() => resolve);
		});
	}, [setNoConfigurationsEnabledModalOpen, logger, setConfirmSaveModalResolve]);

	const confirmClose = useCallback(() => {
		return new Promise<boolean>((resolve) => {
			setUnsavedChangesModalOpen(true);
			void logger?.assessment.edit.configurationModal.warningShown("Unsaved Changes");
			setConfirmCloseModalResolve(() => resolve);
		});
	}, [setUnsavedChangesModalOpen, logger, setConfirmCloseModalResolve]);

	const saveProctoringConfiguration = useCallback(
		async (
			password?: string,
			additionalAssessmentInfo?: Partial<AssessmentInfo>,
			onlyProfile?: boolean,
			onlyOptions?: boolean,
		) => {
			const quizId = quizInfo.quizId;
			if (!quizId) {
				throw new Error("Cannot save configuration without a Quiz ID");
			}
			const options = {
				assessmentInfo: {
					...quizInfo,
					...(additionalAssessmentInfo ?? {}),
					quizId: quizId,
				},
				profileId: onlyOptions ? undefined : currentProfileId,
				proctoringAssessmentConfiguration: {
					...proctoringAssessmentConfiguration,
					...(password ? { password } : {}),
				},
				proctoringOption: onlyProfile ? undefined : proctoringOption,
			};

			void logger?.assessment.edit.configurationModal.saveStarted({
				...options,
				onlyProfile,
				onlyOptions,
			});

			const result = await saveProctoringAssessmentConfiguration(options);

			return { options, result };
		},
		[
			quizInfo,
			currentProfileId,
			proctoringAssessmentConfiguration,
			proctoringOption,
			logger?.assessment.edit.configurationModal,
			saveProctoringAssessmentConfiguration,
		],
	);

	const initiateSaveProcess = useCallback(
		(saveParameters: SaveParameters) => {
			/**
			 * We enter the function body on execution of the parent saveHandler.
			 * Hence, discard previous Save States and initialize new one
			 */
			resetSaveStates();
			const requestPromise = Promise.withResolvers<void>();
			setFinishedPresave(false);
			setSavePromise(requestPromise);
			setSaveParameters({
				password: saveParameters.password,
				additionalAssessmentInfo: saveParameters.additionalAssessmentInfo,
			});
			setSaveRequested(true);
		},
		[resetSaveStates],
	);

	const isProctoringOptionModified = useMemo(() => {
		return hasProctoringOptionsChanged(initialProctoringOption, proctoringOption);
	}, [initialProctoringOption, proctoringOption]);

	const isAssessmentConfigurationModified = useMemo(() => {
		return hasAssessmentConfigurationChanged(
			initialProctoringAssessmentConfiguration,
			proctoringAssessmentConfiguration,
		);
	}, [initialProctoringAssessmentConfiguration, proctoringAssessmentConfiguration]);

	const isAnyProctoringOptionEnabled = useMemo(() => {
		return areProctoringOptionsSet(proctoringOption, defaultProctoringOption);
	}, [defaultProctoringOption, proctoringOption]);

	const closeDisclaimerModal = useCallback(() => {
		setCallReset(true);
		setDisclaimerModalOpen(false);
	}, []);

	const submitDisclaimerModal = useCallback(() => {
		setFinishedPresave(true);
		setDisclaimerModalOpen(false);
	}, []);

	const openDisclaimerModal = useCallback(() => setDisclaimerModalOpen(true), []);

	const closeUnsavedChangesModal = useCallback(() => {
		confirmCloseModalResolve?.(false);
		setUnsavedChangesModalOpen(false);
	}, [confirmCloseModalResolve]);

	const confirmUnsavedChangesModal = useCallback(() => {
		confirmCloseModalResolve?.(true);
		setUnsavedChangesModalOpen(false);
	}, [confirmCloseModalResolve]);

	const closeNoConfigModal = useCallback(() => {
		confirmSaveModalResolve?.(false);
		setNoConfigurationsEnabledModalOpen(false);
	}, [confirmSaveModalResolve]);

	const confirmNoConfigModal = useCallback(() => {
		confirmSaveModalResolve?.(true);
		setNoConfigurationsEnabledModalOpen(false);
	}, [confirmSaveModalResolve]);

	return {
		state: {
			callReset,
			saveParameters,
			savePromise,
			saveRequested,
			finishedPresave,
			noConfigurationsEnabledModalOpen,
			unsavedChangesModalOpen,
			isProctoringOptionModified,
			isAssessmentConfigurationModified,
			isAnyProctoringOptionEnabled,
			disclaimerModalOpen,
		},
		action: {
			resetSaveStates,
			completePresave,
			saveProctoringConfiguration,
			confirmSave,
			confirmClose,
			openDisclaimerModal,
			closeDisclaimerModal,
			submitDisclaimerModal,
			closeUnsavedChangesModal,
			confirmUnsavedChangesModal,
			closeNoConfigModal,
			confirmNoConfigModal,
			initiateSaveProcess,
		},
	};
}
